import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Theme } from '@carbon/react';
import { Calendar, ChevronLeft, ChevronRight, Close, Location, Time } from '@carbon/icons-react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  MONTHS,
  WEEKDAYS,
  dateKeyInZone,
  formatLongDate,
  formatShortDate,
  formatTime,
} from './workDayUtils';

// Najranije početno vreme smene za dati dan (CET/CEST)
const earliestStartTime = (dayShifts) => {
  if (!dayShifts || dayShifts.length === 0) return null;
  let earliest = null;
  dayShifts.forEach((shift) => {
    const d = new Date(shift?.startTime);
    if (Number.isNaN(d.getTime())) return;
    if (!earliest || d.getTime() < earliest.getTime()) earliest = d;
  });
  if (!earliest) return null;
  return formatTime(earliest);
};

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const STATUS_LABELS = {
  planned: 'Planirana',
  'in-progress': 'U toku',
  completed: 'Završena',
  canceled: 'Otkazana',
  cancelled: 'Otkazana',
};

const translateStatus = (status) => STATUS_LABELS[status] || status || '-';

const InfoRow = ({ label, value }) => (
  <div className="wdash-detail__row">
    <span className="wdash-detail__label">{label}</span>
    <span className="wdash-detail__value">{value}</span>
  </div>
);

/**
 * MySchedulePanel - "Moj raspored": mesečni kalendar smena zaposlenog sa legendom.
 * Klik na dan sa smenama otvara desni panel sa smenama tog dana / detaljima smene.
 */
const MySchedulePanel = ({
  shifts = [],
  loading = false,
  year,
  month,
  onMonthChange,
  todayShift = null,
}) => {
  const { theme } = useTheme();

  // Grupisanje smena po CET/CEST datumu početka smene
  const shiftsByDay = useMemo(() => {
    const map = new Map();
    (Array.isArray(shifts) ? shifts : []).forEach((shift) => {
      const d = new Date(shift?.startTime);
      if (Number.isNaN(d.getTime())) return;
      const key = dateKeyInZone(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(shift);
    });
    return map;
  }, [shifts]);

  const todayKey = dateKeyInZone(new Date());

  // Izabrani dan i smena za desni panel sa detaljima
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);

  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const days = [];
  for (let day = 1; day <= daysInMonth; day += 1) {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    days.push({ day, key, shifts: shiftsByDay.get(key) || [] });
  }

  // Pomeraj: ponedeljak je prva kolona
  const firstWeekday = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const offset = firstWeekday === 0 ? 6 : firstWeekday - 1;

  const changeMonth = (direction) => {
    const d = new Date(Date.UTC(year, month + direction, 1));
    onMonthChange?.({ year: d.getUTCFullYear(), month: d.getUTCMonth() });
    setSelectedKey(null);
    setSelectedShift(null);
  };

  // Otvaranje desnog panela sa detaljima za izabrani dan
  const openDay = (key, dayShifts) => {
    setSelectedKey(key);
    // Ako dan ima samo jednu smenu - odmah prikaži njene detalje
    setSelectedShift(dayShifts.length === 1 ? dayShifts[0] : null);
  };

  const closePanel = () => {
    setSelectedKey(null);
    setSelectedShift(null);
  };

  const backToList = () => setSelectedShift(null);

  const dayShifts = selectedKey ? (shiftsByDay.get(selectedKey) || []) : [];
  const isPanelOpen = Boolean(selectedKey);

  // Info linija: izabrani dan (a ako nije izabran - današnja smena)
  const infoShift = useMemo(() => {
    if (selectedKey && shiftsByDay.has(selectedKey)) {
      return { key: selectedKey, shifts: shiftsByDay.get(selectedKey) };
    }
    return null;
  }, [selectedKey, shiftsByDay]);

  const renderInfo = () => {
    if (infoShift) {
      const first = infoShift.shifts[0];
      const [y, m, d] = infoShift.key.split('-').map(Number);
      const date = new Date(Date.UTC(y, m - 1, d));
      return `${formatShortDate(date)} · ${formatTime(first.startTime)}–${formatTime(first.endTime)}${first.area ? ` · ${first.area}` : ''}`;
    }
    if (todayShift) {
      return `${formatShortDate(new Date())} · ${formatTime(todayShift.startTime)}–${formatTime(todayShift.endTime)}${todayShift.area ? ` · ${todayShift.area}` : ''}`;
    }
    return null;
  };

  const infoText = renderInfo();

  return (
    <section className="wdash-panel wdash-schedule">
      <div className="wdash-schedule__head">
        <span className="wdash-panel__icon">
          <Calendar size={20} />
        </span>
        <h2 className="wdash-schedule__title">Moj raspored</h2>
        <div className="wdash-schedule__nav">
          <button type="button" onClick={() => changeMonth(-1)} aria-label="Prethodni mesec">
            <ChevronLeft size={18} />
          </button>
          <button type="button" onClick={() => changeMonth(1)} aria-label="Sledeći mesec">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <p className="wdash-schedule__month">
        {capitalize(MONTHS[month])} {year}
      </p>

      <div className="wdash-cal" aria-busy={loading}>
        <div className="wdash-cal__weekdays">
          {WEEKDAYS.map((day) => (
            <span key={day} className="wdash-cal__weekday">{day}</span>
          ))}
        </div>

        <div className="wdash-cal__grid">
          {Array.from({ length: offset }).map((_, idx) => (
            <div key={`empty-${idx}`} className="wdash-cal__cell wdash-cal__cell--empty" />
          ))}

          {days.map((day) => {
            const hasShifts = day.shifts.length > 0;
            const isToday = day.key === todayKey;
            const isSelected = day.key === selectedKey;
            return (
              <button
                key={day.key}
                type="button"
                className={`wdash-cal__cell ${hasShifts ? 'has-shifts' : ''} ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''}`}
                onClick={() => hasShifts && openDay(day.key, day.shifts)}
                disabled={!hasShifts}
              >
                <span className="wdash-cal__num">{day.day}</span>
                {hasShifts && <span className="wdash-cal__time">{earliestStartTime(day.shifts)}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="wdash-schedule__legend">
        <span className="wdash-legend__item">
          <span className="wdash-legend__dot" />
          Radni dan
        </span>
        <span className="wdash-legend__item">
          <span className="wdash-legend__ring" />
          Danas
        </span>
      </div>

      {infoText && <p className="wdash-schedule__info">{infoText}</p>}

      {/* Desni panel sa detaljima dana/smene (portal na body) */}
      {ReactDOM.createPortal(
        <>
          <Theme theme={theme}>
            <div className={`wdash-drawer ${isPanelOpen ? 'open' : ''}`} aria-hidden={!isPanelOpen}>
              <div className="wdash-drawer__head">
                {selectedShift && dayShifts.length > 1 && (
                  <button
                    type="button"
                    className="wdash-drawer__icon-btn"
                    onClick={backToList}
                    aria-label="Nazad na listu smena"
                  >
                    <ChevronLeft size={18} />
                  </button>
                )}
                <h3 className="wdash-drawer__title">
                  {selectedShift ? 'Detalji smene' : formatLongDate(selectedKey)}
                </h3>
                <button
                  type="button"
                  className="wdash-drawer__icon-btn"
                  onClick={closePanel}
                  aria-label="Zatvori"
                >
                  <Close size={18} />
                </button>
              </div>

              <div className="wdash-drawer__body">
                {selectedShift ? (
                  <div className="wdash-detail__info">
                    <InfoRow
                      label="Vreme"
                      value={`${formatTime(selectedShift.startTime)}–${formatTime(selectedShift.endTime)}`}
                    />
                    <InfoRow label="Datum" value={formatLongDate(selectedShift.startTime)} />
                    {selectedShift.name && <InfoRow label="Naziv" value={selectedShift.name} />}
                    {selectedShift.area && <InfoRow label="Područje" value={selectedShift.area} />}
                    {selectedShift.employeeFullName && (
                      <InfoRow label="Zaposleni" value={selectedShift.employeeFullName} />
                    )}
                    {selectedShift.employeeRole && (
                      <InfoRow label="Uloga" value={selectedShift.employeeRole} />
                    )}
                    <InfoRow label="Status" value={translateStatus(selectedShift.status)} />
                  </div>
                ) : dayShifts.length > 0 ? (
                  dayShifts.map((shift, index) => (
                    <button
                      key={shift.id || `shift-${index}`}
                      type="button"
                      className="wdash-shift-item"
                      onClick={() => setSelectedShift(shift)}
                    >
                      <span className="wdash-shift-item__main">
                        <span className="wdash-shift-item__name">{shift.name || 'Smena'}</span>
                        <span className="wdash-shift-item__meta">
                          <Time size={12} />
                          {formatTime(shift.startTime)}–{formatTime(shift.endTime)}
                        </span>
                        {shift.area && (
                          <span className="wdash-shift-item__meta">
                            <Location size={12} />
                            {shift.area}
                          </span>
                        )}
                      </span>
                      <ChevronRight size={16} className="wdash-shift-item__chevron" />
                    </button>
                  ))
                ) : (
                  <p className="wdash-drawer__empty">Nema smena za izabrani dan</p>
                )}
              </div>
            </div>
          </Theme>

          {isPanelOpen && <div className="wdash-drawer-overlay" onClick={closePanel} />}
        </>,
        document.body
      )}

    </section>
  );
};

export default MySchedulePanel;
